import {faSort, faSortDown, faSortUp} from '@fortawesome/pro-solid-svg-icons';
import styled, {css} from 'styled-components';

import {Icon} from '../../components/Icon/Icon';
import {scToTwColour} from '../../theme';

import type {HeadingParams2, TableSortBy2} from './Table';

type Props<T extends {id: string}> = {
  heading: HeadingParams2<T>;
  sortBy: TableSortBy2 | null;
  handleSort: (sort: TableSortBy2 | null) => void;
  children: React.ReactNode;
};
export function TableHeading2<T extends {id: string}>(
  props: Props<T>
): React.JSX.Element {
  if (!props.heading.sortable) {
    return (
      <StyledHeading width={props.heading.width}>
        {props.children}
      </StyledHeading>
    );
  }

  // handle col sort cycle
  // unsorted -> asc -> desc
  const handleClick = (): void => {
    // first click
    if (!props.sortBy || props.heading.prop !== props.sortBy.prop) {
      props.handleSort({
        prop: props.heading.prop,
        isAsc: true,
      });
      // second click
    } else if (props.sortBy.isAsc) {
      props.handleSort({
        prop: props.heading.prop,
        isAsc: false,
      });
      // final click
    } else {
      props.handleSort(null);
    }
  };

  let icon = faSort;
  if (props.heading.prop === props.sortBy?.prop) {
    if (props.sortBy.isAsc) icon = faSortUp;
    else icon = faSortDown;
  }

  return (
    <StyledHeading width={props.heading.width}>
      <button type="button" onClick={handleClick}>
        <StyledIcon icon={icon} />
        {props.children}
      </button>
    </StyledHeading>
  );
}

export const StyledHeading = styled.th<{width?: string}>`
  color: ${scToTwColour('theme.colours.slate11')};
  white-space: nowrap;
  border-bottom: 1px solid ${scToTwColour('theme.colours.slate6')};
  font-size: 0.875rem /* 14px */;
  line-height: 1rem /* 20px */;

  ${({width}) =>
    !!width &&
    css`
      width: ${width};
    `}

  button {
    display: block;
    width: 100%;
    text-align: left;
    cursor: pointer;

    &:not[role='checkbox'] {
      color: ${scToTwColour('theme.colours.slate12')};
      font-weight: 700;
      background-color: transparent;
      outline: none;
    }
  }

  & button:hover,
  & button:focus {
    color: ${scToTwColour('theme.colours.blue11')};
  }
`;
const StyledIcon = styled(Icon)`
  margin-right: 0.3em;
`;
